import { useState } from "react";
import { router } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import {
  Badge,
  Button,
  EmptyState,
  Panel,
  Select,
  Textarea,
} from "@/components/ui";
import type { Submission } from "@/interfaces/submission";

type QueueEvent = {
  id: number;
  name: string;
  funds_by_sites: boolean;
};

// The queue for sites attendees handed in.
//
// Worked asynchronously, from wherever the reviewer is. Reviewing in the room
// on the day needs enough people awake in the right timezone at the right
// hour, and the only ones reliably there are the organizers, who are the least
// able to judge their own room's work. So this is staffed by reviewers with no
// stake in the event.
export default function SiteReview({
  queue,
  queue_count,
  recent,
  events,
  selected_event_id,
  rate_per_site_cents,
}: {
  queue: Submission[];
  queue_count: number;
  recent: Submission[];
  events: QueueEvent[];
  selected_event_id: number | null;
  rate_per_site_cents: number;
}) {
  return (
    <Layout>
      <div className="flex w-full flex-col gap-6 py-6">
        <PageHeading
          title="Sites"
          subtitle={
            queue_count === 1
              ? "1 site waiting on a verdict."
              : `${queue_count} sites waiting on a verdict.`
          }
          actions={
            events.length > 0 && (
              <Select
                value={selected_event_id ?? ""}
                onChange={(e) =>
                  router.get(
                    "/site-review",
                    e.target.value ? { event_id: e.target.value } : {},
                    { preserveScroll: true },
                  )
                }
                className="max-w-64"
              >
                <option value="">Every city</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </Select>
            )
          }
        />

        {queue.length === 0 ? (
          <EmptyState
            title="Queue's clear"
            description="Nothing is waiting on a verdict right now."
          />
        ) : (
          <div className="flex flex-col gap-5">
            {queue.map((submission) => (
              <SiteRow
                key={submission.id}
                submission={submission}
                eventId={selected_event_id}
                rateCents={rate_per_site_cents}
              />
            ))}
          </div>
        )}

        {recent.length > 0 && (
          <Panel title="you-just-decided.txt" tone="alt">
            <div className="flex flex-col gap-3">
              {recent.map((submission) => (
                <RecentRow
                  key={submission.id}
                  submission={submission}
                  eventId={selected_event_id}
                />
              ))}
            </div>
          </Panel>
        )}
      </div>
    </Layout>
  );
}

function SiteRow({
  submission,
  eventId,
  rateCents,
}: {
  submission: Submission;
  eventId: number | null;
  rateCents: number;
}) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = (verdict: "approved" | "rejected") => {
    if (busy) return;
    setBusy(true);
    router.patch(
      `/site-review/${submission.id}`,
      { verdict, notes, event_id: eventId ?? undefined },
      { preserveScroll: true, onFinish: () => setBusy(false) },
    );
  };

  return (
    <Panel title={`${submission.title}.html`}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-caption text-ink-soft">
            by {submission.author}
          </span>
          {submission.event_name && (
            <Badge tone="neutral">{submission.event_name}</Badge>
          )}
          {/* The one fact that changes what a verdict costs: an international
              event is pre-funded per approved site handed in before the day. */}
          {submission.funds_event ? (
            <Badge
              tone="brand"
              title="This event is pre-funded per approved site"
            >
              worth {formatMoney(submission.worth_cents ?? rateCents)}
            </Badge>
          ) : submission.before_event_start === false ? (
            <Badge
              tone="warning"
              title="Handed in after the event started, so it isn't part of what the event was pre-funded for"
            >
              after the start
            </Badge>
          ) : null}
        </div>

        {submission.description && (
          <p className="text-body text-ink-muted whitespace-pre-line">
            {submission.description}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <a
            // Somebody else's markup: new tab, and never handed this window or
            // this page's referrer.
            href={submission.live_url}
            target="_blank"
            rel="noopener noreferrer nofollow ugc"
            className="text-link text-body font-bold break-all underline"
          >
            Open the site
          </a>
          {submission.hosted && (
            <span className="text-caption text-ink-soft">hosted here</span>
          )}
        </div>

        {submission.screenshot_url && (
          <img
            src={submission.screenshot_url}
            alt={submission.title}
            loading="lazy"
            className="border-line max-h-64 w-full border-2 object-contain"
          />
        )}

        {/* The thing a verdict actually turns on.
            What a reviewer is looking for is whether a person wrote this, and
            the rendered page is the worst place to see that - a generated page
            and a hand-written one look identical in a browser and differ
            completely in the source. So the markup is here, in the row, rather
            than a tab away behind a link most people would not open 66 times.
            Rendered as text in a <pre>: it is somebody else's HTML and it must
            never be parsed by this document. */}
        <CodeView submission={submission} />

        <Textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Why? Required to send back, optional to approve."
        />

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => decide("approved")} disabled={busy} size="sm">
            Approve
          </Button>
          <Button
            onClick={() => decide("rejected")}
            disabled={busy || notes.trim().length === 0}
            variant="secondary"
            size="sm"
          >
            Send back
          </Button>
        </div>
      </div>
    </Panel>
  );
}

// The source of a pasted site, in the row.
//
// Shown open rather than behind a toggle: with sixty-odd in the queue, anything
// that takes a click per row is a step that stops happening, and this is the
// one piece of evidence that separates a page a teenager wrote from a generated
// one. Scrolls instead of growing, so a long file does not push the approve
// button off the screen.
//
// A site that was linked rather than pasted has no source here. That is worth
// saying out loud, because an empty space would otherwise read as "no code" when
// it means "the code is on somebody else's server".
function CodeView({ submission }: { submission: Submission }) {
  const [open, setOpen] = useState(true);

  if (!submission.hosted) {
    return (
      <p className="text-caption text-ink-soft">
        Linked, not pasted - open the site to see it. There's no source to read
        here.
      </p>
    );
  }

  const html = submission.html ?? "";
  const css = submission.css ?? "";
  const lines = html.split("\n").length + (css ? css.split("\n").length : 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display text-caption text-ink font-bold">
          index.html
          {css ? " + style.css" : ""}
        </span>
        <span className="text-caption text-ink-soft">
          {lines} {lines === 1 ? "line" : "lines"}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
          {open ? "Hide code" : "Show code"}
        </Button>
      </div>

      {open && (
        <>
          <pre className="border-line bg-surface-plain text-caption font-literal text-ink max-h-80 overflow-auto border-2 p-3 whitespace-pre-wrap">
            {html}
          </pre>
          {css && (
            <pre className="border-line bg-surface-plain text-caption font-literal text-ink max-h-48 overflow-auto border-2 p-3 whitespace-pre-wrap">
              {css}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

function RecentRow({
  submission,
  eventId,
}: {
  submission: Submission;
  eventId: number | null;
}) {
  const approved = submission.review_state === "approved";

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Badge tone={approved ? "success" : "danger"}>
        {approved ? "approved" : "sent back"}
      </Badge>
      <span className="text-body text-ink font-bold">{submission.title}</span>
      <span className="text-caption text-ink-soft">
        by {submission.author}
        {submission.event_name ? ` at ${submission.event_name}` : ""}
      </span>
      {/* A misclick has to be reversible without going to look for the row. */}
      <Button
        variant="ghost"
        size="sm"
        className="ml-auto"
        onClick={() =>
          router.patch(
            `/site-review/${submission.id}`,
            {
              verdict: approved ? "rejected" : "approved",
              notes: submission.review_notes ?? "Changed on a second look.",
              event_id: eventId ?? undefined,
            },
            { preserveScroll: true },
          )
        }
      >
        {approved ? "Undo, send back" : "Undo, approve"}
      </Button>
    </div>
  );
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}
